import { useState, useEffect, useCallback } from 'react';
import notificationService from '../services/notificationService';

/**
 * useNotifications Hook
 * 
 * What this does:
 * - Manages notification state
 * - Polls for new notifications every 30 seconds
 * - Provides functions to mark as read, delete, etc.
 * 
 * Usage:
 * const { notifications, unreadCount, markAsRead } = useNotifications();
 */

const useNotifications = (pollInterval = 30000) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Load notifications
  const loadNotifications = useCallback(async () => {
    try {
      const [notifs, count] = await Promise.all([
        notificationService.getAll(false, 50),
        notificationService.getUnreadCount()
      ]);
      setNotifications(notifs);
      setUnreadCount(count);
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Poll for new notifications
  useEffect(() => {
    const interval = setInterval(() => {
      loadNotifications();
    }, pollInterval);

    return () => clearInterval(interval);
  }, [loadNotifications, pollInterval]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      await loadNotifications();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  }, [loadNotifications]);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      await loadNotifications();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  }, [loadNotifications]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await notificationService.delete(notificationId);
      await loadNotifications();
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  }, [loadNotifications]);

  // Delete all read notifications
  const deleteAllRead = useCallback(async () => {
    try {
      await notificationService.deleteAllRead();
      await loadNotifications();
    } catch (err) {
      console.error('Error deleting read notifications:', err);
    }
  }, [loadNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    reload: loadNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllRead
  };
};

export default useNotifications;
